/**
 * The kit's segmented control (`.seg`): a radiogroup with a sliding thumb — Blockari's `Segmented` (shell/ui.tsx), as the
 * leaderboard and the streaks sheet use it. Equal cells by default; with `fit` (c258375) each cell is as wide as its label
 * plus equal padding, the spare room shared out evenly, and the thumb is measured from the checked cell wherever the
 * layout put it (a row of many short labels: the five leaderboard boards).
 */

import { el } from '@/ui/hud';

export interface SegOption<T> {
  readonly value: T;
  readonly label: string;
  /** The cell's accessible name when its visible text reads better as another phrase ("$CHAIN" for "$CHAIN won"). */
  readonly ariaLabel?: string;
}

export interface Segmented<T> {
  readonly root: HTMLElement;
  set(v: T): void;
}

export function segmented<T extends string>(
  label: string,
  options: readonly SegOption<T>[],
  onChange: (v: T) => void,
  opts: { fit?: boolean; tone?: 'warm' } = {},
): Segmented<T> {
  const fit = opts.fit === true;
  const root = el('div', `w21-seg${opts.tone === 'warm' ? ' w21-warm' : ''}${fit ? ' w21-fit' : ''}`);
  root.setAttribute('role', 'radiogroup');
  root.setAttribute('aria-label', label);
  root.style.setProperty('--n', String(options.length));
  const thumb = el('span', 'w21-seg-thumb');
  thumb.setAttribute('aria-hidden', 'true');
  root.append(thumb);
  const items = options.map((o, i) => {
    const b = el('button', 'w21-seg-item', o.label) as HTMLButtonElement;
    b.type = 'button';
    b.setAttribute('role', 'radio');
    if (o.ariaLabel) b.setAttribute('aria-label', o.ariaLabel);
    b.addEventListener('click', () => onChange(o.value));
    b.addEventListener('keydown', (e) => {
      const n = options.length;
      let next = -1;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % n;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + n) % n;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = n - 1;
      if (next < 0) return;
      e.preventDefault();
      const o2 = options[next];
      if (!o2) return;
      onChange(o2.value);
      items[next]?.focus();
    });
    root.append(b);
    return b;
  });

  let idx = 0;
  /** `fit`: the thumb sits on the checked cell. */
  const place = (): void => {
    const cell = items[idx];
    if (!cell || cell.offsetWidth === 0) return;
    root.style.setProperty('--tx', `${cell.offsetLeft - 4}px`);
    root.style.setProperty('--tw', `${cell.offsetWidth}px`);
  };
  if (fit && typeof ResizeObserver !== 'undefined') {
    // Whenever the row or a cell changes size (shown, resized, a font landing) the thumb is measured again.
    const ro = new ResizeObserver(place);
    ro.observe(root);
    for (const b of items) ro.observe(b);
    void document.fonts?.ready.then(place);
  }

  return {
    root,
    set(v) {
      idx = Math.max(0, options.findIndex((o) => o.value === v));
      root.style.setProperty('--i', String(idx));
      items.forEach((b, i) => {
        b.setAttribute('aria-checked', String(i === idx));
        b.tabIndex = i === idx ? 0 : -1;
      });
      if (fit) place();
    },
  };
}
